import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Star, Gift, ArrowLeft, DollarSign, CheckCircle, 
  Clock, MessageSquare, Phone, User
} from "lucide-react";

const CASHBACK_AMOUNT = 2;

const AdminReviewsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");

  useEffect(() => {
    if (user?.is_admin) {
      fetchReviews();
    }
  }, [user]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/all`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Error al cargar reviews");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkCashbackSent = async (reviewId) => {
    setProcessing(reviewId);
    try {
      await axios.post(`${API}/reviews/${reviewId}/mark-cashback-sent`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Cashback marcado como enviado");
      // Update local state
      setReviews(prev => prev.map(r => 
        r.review_id === reviewId 
          ? { ...r, cashback_status: "sent", cashback_sent_at: new Date().toISOString() }
          : r
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al marcar cashback");
    } finally {
      setProcessing(null);
    }
  };

  const pendingReviews = reviews.filter(r => r.cashback_status === "pending");
  const sentReviews = reviews.filter(r => r.cashback_status === "sent");

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-PA", { 
      day: "numeric", 
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#E2E8F0]"}
          />
        ))}
      </div>
    );
  };

  const ReviewCard = ({ review, showCashbackButton = false }) => (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-[#F1F5F9] flex items-center justify-center overflow-hidden flex-shrink-0">
          {review.user_picture ? (
            <img src={review.user_picture} alt={review.user_name} className="w-full h-full object-cover" />
          ) : (
            <User size={24} className="text-[#64748B]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className="font-semibold text-[#0F172A] truncate">{review.user_name}</h4>
            {renderStars(review.rating)}
          </div>
          <p className="text-xs text-[#64748B]">{formatDate(review.created_at)}</p>
        </div>
      </div>

      {/* Product */}
      <div className="flex items-center gap-3 p-3 bg-[#F1F5F9] rounded-xl">
        <img
          src={review.product_image}
          alt={review.product_title}
          className="w-14 h-14 object-cover rounded-lg"
        />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-[#0F172A] truncate">{review.product_title}</p>
          <p className="text-sm text-[#64748B]">Review del producto</p>
        </div>
      </div>

      {/* Comment */}
      <div className="p-3 bg-[#FAFAF9] rounded-xl">
        <div className="flex items-start gap-2">
          <MessageSquare size={16} className="text-[#64748B] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-[#0F172A]">{review.comment}</p>
        </div>
      </div>

      {/* Yappy Number & Cashback */}
      {showCashbackButton && (
        <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img 
                src="https://www.yappy.com.pa/images/logo-yappy.svg" 
                alt="Yappy" 
                className="h-6"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span className="font-bold text-[#0F172A]">{review.yappy_number}</span>
            </div>
            <span className="text-lg font-bold text-[#10B981]">${CASHBACK_AMOUNT}</span>
          </div>
          
          <Button
            onClick={() => handleMarkCashbackSent(review.review_id)}
            disabled={processing === review.review_id}
            className="w-full rounded-full h-11 bg-[#10B981] hover:bg-[#059669]"
            data-testid={`mark-cashback-${review.review_id}`}
          >
            {processing === review.review_id ? (
              <div className="spinner" style={{ width: 20, height: 20 }} />
            ) : (
              <>
                <DollarSign size={18} className="mr-2" />
                Marcar Cashback Enviado
              </>
            )}
          </Button>
          
          <a
            href={`https://wa.me/507${review.yappy_number?.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`¡Hola! Tu cashback de $${CASHBACK_AMOUNT} por tu review en Clueer ha sido enviado a tu Yappy. ¡Gracias por tu compra!`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-sm text-[#34D399] hover:underline"
          >
            <Phone size={14} />
            Contactar por WhatsApp
          </a>
        </div>
      )}

      {/* Cashback Sent Badge */}
      {review.cashback_status === "sent" && (
        <div className="flex items-center justify-between p-3 bg-[#10B981]/10 rounded-xl">
          <div className="flex items-center gap-2">
            <CheckCircle size={18} className="text-[#10B981]" />
            <span className="text-sm font-medium text-[#10B981]">Cashback enviado</span>
          </div>
          <span className="text-xs text-[#64748B]">{formatDate(review.cashback_sent_at)}</span>
        </div>
      )}
    </div>
  );

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <p className="text-[#64748B]">Acceso denegado</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-6"
        >
          <ArrowLeft size={20} />
          Volver
        </button>

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Gift className="h-8 w-8 text-[#10B981]" />
            <div>
              <h1 className="text-2xl font-bold text-[#0F172A]">Reviews & Cashback</h1>
              <p className="text-sm text-[#64748B]">Gestiona los reviews y pagos de cashback</p>
            </div>
          </div>
          {pendingReviews.length > 0 && (
            <span className="px-3 py-1 bg-[#F59E0B] text-white rounded-full text-sm font-medium">
              {pendingReviews.length} pendientes
            </span>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock size={16} />
              Pendientes ({pendingReviews.length})
            </TabsTrigger>
            <TabsTrigger value="sent" className="flex items-center gap-2">
              <CheckCircle size={16} />
              Enviados ({sentReviews.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : pendingReviews.length === 0 ? (
              <div className="text-center py-12">
                <Gift size={64} className="mx-auto text-[#CBD5E1] mb-4" />
                <h3 className="text-lg font-semibold text-[#0F172A]">No hay reviews pendientes</h3>
                <p className="text-[#64748B]">Los reviews con cashback pendiente aparecerán aquí</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingReviews.map((review) => (
                  <ReviewCard key={review.review_id} review={review} showCashbackButton />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sent">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : sentReviews.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle size={64} className="mx-auto text-[#CBD5E1] mb-4" />
                <h3 className="text-lg font-semibold text-[#0F172A]">No hay cashbacks enviados</h3>
                <p className="text-[#64748B]">Los reviews con cashback enviado aparecerán aquí</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sentReviews.map((review) => (
                  <ReviewCard key={review.review_id} review={review} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <BottomNav />
    </div>
  );
};

export default AdminReviewsPage;
